import React, { useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { lensQueriedAtLabel } from '~/utils/lensQueriedAt';

import AgentHubOffers from './AgentHubOffers';
import CommercialKpiFilters from './CommercialKpiFilters';
import CommercialKpiOffers from './CommercialKpiOffers';
import CommercialKpiTargets from './CommercialKpiTargets';
import PerformanceChart from './PerformanceChart';
import ScopeSelect from './ScopeSelect';
import {
  adaptKpi,
  exportKpi,
  readLiveFilters,
  readLiveView,
  requestFilters,
  retainedNativeFilters,
  validScope,
  writeLiveFilters,
} from './liveDataAdapter';
import {
  Filters,
  METRIC_IDS,
  Model,
  REPORT_VIEWS,
  SelectionTuple,
  VIEW_FORMULAS,
  VIEW_TITLES,
  cardsForView,
  defaults,
  format,
  offerDisplayKey,
  parseSelection,
  quarterLabel,
  quarterProgress,
  toggleTargetSelection,
} from './presentationModel';

const FILTER_LABELS: Partial<Record<keyof Filters, string>> = {
  quarter: 'Quarter',
  manager: 'RSM',
  region: 'PCM region',
  category: 'Category',
  person: 'PCM',
  refund: 'Refund',
  brand: 'Brand',
  product: 'Product',
  country: 'Offer country',
  offer: 'Offer ID',
  rsmRegion: 'RSM region',
  opportunity: 'Opportunity',
  offerName: 'Offer name',
  bdm: 'BDM',
};
const FORMULAS = {
  actual: 'SUM(actual_value) for the selected metric and quarter. Missing source values remain Unknown.',
  target: 'SUM(target_value). A recorded zero is 0; an absent target is Unknown. Source zero-target ratios are 0.',
  forecast:
    'Actual ÷ elapsed calendar days × quarter calendar days, using the injected Sydney civil date for the current request.',
  onTrack:
    'Yes when forecast is at least target, or when the recorded target is zero. Missing source values remain Unknown.',
};

/** Reusable dashboard content. Router, QueryClient, MUI theme and navigation belong to the host. */
export default function CommercialKpiTrackerContent() {
  const now = new Date();
  const [params, setParams] = useSearchParams();
  const [methodOpen, setMethodOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [offerDetailIdentity, setOfferDetailIdentity] = useState('');
  const [offerDetail, setOfferDetail] = useState<Model['displayedOfferRows'][number] | null>(null);
  const filters = readLiveFilters(params.toString(), now);
  const view = readLiveView(params);
  useEffect(() => {
    if (['view', 'metric', 'section'].some((key) => params.has(key))) {
      setParams(writeLiveFilters(params, {}, readLiveView(params), new Date()), { replace: true });
    }
  }, [params, setParams]);
  const retained = retainedNativeFilters(params.toString());
  const request = requestFilters(filters, view, now, retained);
  const query = useGetReportingLeLensData(
    validScope(filters, view) ? nativeLensId('commercial-kpi-tracker') : undefined,
    request,
    true,
    undefined,
    undefined,
    undefined,
    cardsForView(view),
  );
  const queriedAt = lensQueriedAtLabel(query.data?.cards);
  const settled = adaptKpi(
    filters,
    view,
    { ...query, cards: query.data?.cards },
    query.data?.filterOptions ?? {},
    now,
    request,
  );
  const model = settled.model,
    targetModel = settled.targetModel;
  const ready = settled.ready;
  const underlyingOffers = useMemo(
    () => (model && offerDetail ? model.offerRows.filter((row) => offerDisplayKey(row, view) === offerDetail.key) : []),
    [model, offerDetail, view],
  );
  useEffect(() => setOfferDetail(null), [settled.identity]);
  const schema: Record<string, PageField> = {
    view: { type: 'enum', values: [...REPORT_VIEWS] },
    quarter: { type: 'date' },
    metric: { type: 'enum', values: [...METRIC_IDS] },
    refund: { type: 'enum', values: ['Before Refund', 'After Refund'] },
    section: { type: 'enum', values: ['Scorecard', 'Offers'] },
    selection: { type: 'text', maxLength: 20000 },
    ...Object.fromEntries(
      Object.entries({
        manager: targetModel.options.managers,
        person: targetModel.options.people,
        region: targetModel.options.regions,
        rsmRegion: targetModel.options.rsmRegions,
        category: targetModel.options.categories,
        brand: targetModel.options.brands,
        product: targetModel.options.products,
        country: targetModel.options.countries,
        opportunity: targetModel.options.opportunities,
        offerName: targetModel.options.offerNames,
        bdm: targetModel.options.bdms,
      }).map(([key, values]) => [key, { type: 'enum', values: ['All', ...values] }]),
    ),
    offer: { type: 'text', maxLength: 200 },
  };
  usePageBridge({
    pageId: 'commercial-kpi-tracker',
    view: {
      title: 'Commercial KPI Tracker · ' + view,
      ready,
      filters: { ...filters, view },
      filterSchema: schema,
      forecastAsOf: settled.forecastAsOf,
      sourceAsOf: settled.sourceAsOf,
      summary: model.metrics,
      visibleSections: model.targetRows.slice(0, 25),
      totalTargetRows: model.targetRows.length,
      visibleRowsAreSample: model.targetRows.length > 25,
      selectedMark: parseSelection(filters.selection),
      retainedNativeFilters: retained,
    },
    edit: (changes) => {
      if (!ready) {
        throw new Error('Commercial KPI data is not ready.');
      }
      validatePageFilters(changes, schema);
      if ('quarter' in changes && !validScope({ ...filters, quarter: String(changes.quarter) }, view)) {
        throw new Error('Quarter must begin on 1 January, April, July or October.');
      }
      const patch: Partial<Filters> = {};
      for (const key of Object.keys(filters) as Array<keyof Filters>) {
        if (typeof changes[key] === 'string') {
          patch[key] = changes[key];
        }
      }
      update(patch, typeof changes.view === 'string' ? changes.view : view);
    },
  });
  function downloadCsv(rows: Array<Record<string, unknown>>, name: string) {
    if (!ready) {
      return;
    }
    const content = exportKpi(rows, filters, view, settled);
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function update(patch: Partial<Filters>, nextView: string = view) {
    const next = writeLiveFilters(params, patch, nextView, now);
    if (Object.keys(patch).length === Object.keys(defaults(now)).length) {
      Object.keys(retained).forEach((key) => next.delete('ck_' + key));
    }
    setParams(next, { replace: true });
  }
  function setDisplay(values: Record<string, string>) {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => next.set(key, value));
    setParams(next, { replace: true });
  }
  function sortTable(table: 'target' | 'offer', key: string) {
    const direction = params.get(`${table}Sort`) === key && params.get(`${table}Dir`) !== 'desc' ? 'desc' : 'asc';
    setDisplay({ [`${table}Sort`]: key, [`${table}Dir`]: direction, ...(table === 'offer' ? { offerPage: '0' } : {}) });
  }
  function selectTarget(tuple: SelectionTuple, multi: boolean) {
    if (!ready) {
      return;
    }
    update({ selection: toggleTargetSelection(filters.selection, tuple, multi) });
  }
  const selections = parseSelection(filters.selection);
  const activeKeys: Array<keyof Filters> = [
    'quarter',
    'manager',
    'region',
    'category',
    'person',
    'refund',
    'brand',
    'product',
    'country',
    'offer',
    'rsmRegion',
    'opportunity',
    'offerName',
    'bdm',
  ];
  const initial = defaults(now);
  const active = activeKeys.filter((field) => filters[field] !== initial[field]);
  const activeCount = active.length + selections.length + Object.keys(retained).length;
  const pageValue = Number(params.get('offerPage') ?? 0);
  const offerPage = Number.isInteger(pageValue) && pageValue >= 0 ? pageValue : 0;
  return (
    <Container maxWidth="xl" sx={{ p: { xs: 1.25, md: 2 }, minWidth: 0 }}>
      <Helmet>
        <title>Commercial KPI Tracker</title>
      </Helmet>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 1.25,
          height: 'auto',
          alignItems: 'start',
          minHeight: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={0.5}>
              <Typography component="h1" sx={{ fontSize: 22, lineHeight: 1.3, fontWeight: 600, color: 'primary.main' }}>
                {VIEW_TITLES[view]}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                <PageChatButton />
                <Button size="small" onClick={() => setFiltersOpen(true)} aria-haspopup="dialog">
                  Filters{activeCount ? ` · ${activeCount} active` : ''}
                </Button>
                {activeCount > 0 && (
                  <Button size="small" onClick={() => update(defaults(now))}>
                    Reset all
                  </Button>
                )}
                <Button size="small" onClick={() => setMethodOpen(true)}>
                  Method and sources
                </Button>
              </Stack>
            </Stack>
            <Typography variant="caption" display="block">
              {VIEW_FORMULAS[view]}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                {quarterLabel(filters.quarter)} · Quarter progress{' '}
                {format(quarterProgress(filters.quarter, settled.forecastAsOf), 'percent')} ·{' '}
                {queriedAt ? queriedAt + ' · ' : ''}forecast as-of {settled.forecastAsOf} (Sydney)
              </Typography>
              <Tabs
                value={view}
                onChange={(_, value) => update({ ...defaults(now), quarter: filters.quarter }, value)}
                aria-label="Tracker dashboard"
                sx={{ minHeight: 28, '& .MuiTab-root': { minHeight: 28, py: 0.25, px: 1, minWidth: 45, fontSize: 12 } }}
              >
                {REPORT_VIEWS.map((item) => (
                  <Tab key={item} label={item} value={item} />
                ))}
              </Tabs>
            </Stack>
            {activeCount > 0 && (
              <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
                {Object.entries(retained).map(([key, values]) => (
                  <Chip
                    key={key}
                    size="small"
                    label={key + ': ' + values.join(', ')}
                    onDelete={() => {
                      const next = new URLSearchParams(params);
                      next.delete('ck_' + key);
                      setParams(next, { replace: true });
                    }}
                  />
                ))}
                {active.map((field) => (
                  <Chip
                    key={field}
                    size="small"
                    title={`${FILTER_LABELS[field] ?? field}: ${filters[field]}`}
                    label={`${FILTER_LABELS[field] ?? field}: ${field === 'quarter' ? quarterLabel(filters[field]) : filters[field]}`}
                    onDelete={() =>
                      update({ [field]: initial[field], ...(field === 'quarter' ? { selection: '[]' } : {}) })
                    }
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
                {selections.length > 0 && (
                  <Chip
                    size="small"
                    label={`${selections.length} selected owner/region groups`}
                    onDelete={() => update({ selection: '[]' })}
                  />
                )}
              </Stack>
            )}
            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 12 }}>
              PCM, MM, Experience and Agent Hub dashboards of the Tableau Commercial KPI Tracker. Subscriptions and
              Subscriptions - MM are email layouts of PCM and MM and are not ported; use a scheduled email instead.
            </Typography>
            {ready && model.agentHubWarning && (
              <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { fontSize: 12 } }}>
                {model.agentHubWarning}
              </Alert>
            )}
            {ready &&
              model.current.id === 'net_margin_adjusted' &&
              model.current.actual !== null &&
              model.offerTotals.netMargin !== null &&
              Math.abs(model.current.actual - model.offerTotals.netMargin) > 0.01 && (
                <Alert
                  severity="warning"
                  icon={false}
                  variant="outlined"
                  sx={{
                    py: 0,
                    px: 0.75,
                    borderColor: 'primary.main',
                    borderLeft: 3,
                    borderLeftColor: BRAND_COLORS.coralGreen,
                    color: 'text.primary',
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? BRAND_COLORS.jetBlack : '#F3F1EA'),
                    '& .MuiAlert-message': { py: 0.5, fontSize: 12, lineHeight: 1.45 },
                  }}
                >
                  <strong>Source reconciliation: </strong>
                  Offer net margin A${format(model.offerTotals.netMargin, 'money')} and KPI net margin A$
                  {format(model.metrics.find((metric) => metric.id === 'net_margin_adjusted')?.actual, 'money')} are
                  separate source populations; reconcile before treating them as interchangeable.
                </Alert>
              )}
          </Box>
          {!ready && (
            <Alert severity={query.error ? 'error' : query.isFetching ? 'info' : 'warning'}>
              {query.error?.message ?? settled.problem}
              {query.error && <Button onClick={() => void query.refetch()}>Retry</Button>}
            </Alert>
          )}
          {!model.valid ? (
            <Alert severity="error">
              Unsupported quarter, metric, refund basis or legacy section in this URL.{' '}
              <Button onClick={() => update(defaults(now))}>Reset filters</Button>
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateRows: 'auto auto',
                gap: 2,
                minHeight: 0,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderTop: 3,
                  borderTopColor: 'primary.main',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  useFlexGap
                  flexWrap="wrap"
                  gap={0.5}
                  sx={{
                    px: 1.5,
                    py: 1,
                    flexShrink: 0,
                    bgcolor: BRAND_COLORS.oceanTeal,
                    color: BRAND_COLORS.compassWhite,
                    '& .MuiButton-root': { color: BRAND_COLORS.compassWhite },
                    '& .MuiChip-root': { bgcolor: BRAND_COLORS.compassWhite, color: BRAND_COLORS.jetBlack },
                    '& .MuiChip-deleteIcon': { color: BRAND_COLORS.jetBlack },
                    '& .MuiOutlinedInput-root': { bgcolor: BRAND_COLORS.compassWhite, color: BRAND_COLORS.jetBlack },
                    '& .MuiSelect-icon': { color: BRAND_COLORS.jetBlack },
                    '& .MuiInputLabel-root': {
                      color: BRAND_COLORS.oceanTeal,
                      bgcolor: BRAND_COLORS.compassWhite,
                      px: 0.25,
                    },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="inherit" fontWeight={600}>
                      KPIs Progress
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 12 }}>
                      Click PCM to filter. Ctrl, ⌘ or Shift adds an owner/region tuple. Activity data comes from the
                      authenticated live source.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {params.get('targetSort') && params.get('targetSort') !== 'source' && (
                      <Button size="small" onClick={() => setDisplay({ targetSort: 'source', targetDir: 'asc' })}>
                        Original order
                      </Button>
                    )}
                    {selections.length > 0 && (
                      <Chip
                        size="small"
                        label={`${selections.length} selected`}
                        onDelete={() => update({ selection: '[]' })}
                      />
                    )}
                    <Button
                      size="small"
                      disabled={!ready}
                      onClick={() =>
                        downloadCsv(
                          model.targetRows,
                          `commercial-kpi-${view.toLowerCase().replace(' ', '-')}-targets-${filters.quarter}.csv`,
                        )
                      }
                    >
                      {selections.length ? 'Export selected KPIs' : 'Export KPIs'}
                    </Button>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Height follows the rows up to the scroll cap, so a filtered table leaves no blank panel. */}
                  <Box sx={{ minWidth: 0 }}>
                    <CommercialKpiTargets
                      rows={targetModel.targetRows}
                      totalRows={targetModel.teamTotalRows}
                      view={view}
                      selection={filters.selection}
                      sort={params.get('targetSort') ?? 'source'}
                      direction={params.get('targetDir') === 'desc' ? 'desc' : 'asc'}
                      onSort={(key) => sortTable('target', key)}
                      onSelect={selectTarget}
                    />
                  </Box>
                  <PerformanceChart model={model} view={view} quarter={filters.quarter} asOf="Unknown" />
                </Box>
              </Paper>
              <Paper
                variant="outlined"
                sx={{
                  minWidth: 0,
                  minHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderTop: 3,
                  borderTopColor: 'primary.main',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  useFlexGap
                  gap={0.5}
                  sx={{
                    px: 1.5,
                    py: 1,
                    flexShrink: 0,
                    bgcolor: BRAND_COLORS.oceanTeal,
                    color: BRAND_COLORS.compassWhite,
                    '& .MuiButton-root': { color: BRAND_COLORS.compassWhite },
                    '& .MuiChip-root': { bgcolor: BRAND_COLORS.compassWhite, color: BRAND_COLORS.jetBlack },
                    '& .MuiChip-deleteIcon': { color: BRAND_COLORS.jetBlack },
                    '& .MuiOutlinedInput-root': { bgcolor: BRAND_COLORS.compassWhite, color: BRAND_COLORS.jetBlack },
                    '& .MuiSelect-icon': { color: BRAND_COLORS.jetBlack },
                    '& .MuiInputLabel-root': {
                      color: BRAND_COLORS.oceanTeal,
                      bgcolor: BRAND_COLORS.compassWhite,
                      px: 0.25,
                    },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="inherit" fontWeight={600}>
                      {view === 'Experience'
                        ? 'Offers - Experience'
                        : view === 'Agent Hub'
                          ? 'Offers - Agent Hub'
                          : 'Offer Details'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 12 }}>
                      {view === 'Agent Hub'
                        ? `A$ · ${model.agentHubOffers.length.toLocaleString('en-AU')} offer rows · analytics.bdm_tracker_offers_ah · quarter ${filters.quarter}`
                        : `A$ · ${model.displayedOfferRows.length.toLocaleString('en-AU')} displayed groups from ${model.offerRows.length.toLocaleString('en-AU')} aggregate records · analytics.pcm_tracker_offers · quarter ${filters.quarter}`}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    {params.get('offerSort') && params.get('offerSort') !== 'source' && (
                      <Button
                        size="small"
                        onClick={() => setDisplay({ offerSort: 'source', offerDir: 'asc', offerPage: '0' })}
                      >
                        Original order
                      </Button>
                    )}
                    {view === 'Agent Hub' && (
                      <Box sx={{ minWidth: 200 }}>
                        <ScopeSelect
                          label="Bdm Name Selection"
                          value={filters.bdm}
                          values={['All', ...targetModel.options.bdms]}
                          onChange={(bdm) => update({ bdm })}
                        />
                      </Box>
                    )}
                    <TextField
                      select
                      size="small"
                      label="Refund"
                      value={filters.refund}
                      onChange={(event) => update({ refund: event.target.value })}
                      sx={{ minWidth: 145, '& .MuiInputBase-root': { fontSize: 12 } }}
                    >
                      {['Before Refund', 'After Refund'].map((basis) => (
                        <MenuItem key={basis} value={basis}>
                          {basis}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      disabled={!ready}
                      onClick={() =>
                        downloadCsv(
                          view === 'Agent Hub' ? model.agentHubOffers : model.displayedOfferRows,
                          `commercial-kpi-${view.toLowerCase().replace(' ', '-')}-offers-${filters.quarter}.csv`,
                        )
                      }
                    >
                      Export offers
                    </Button>
                  </Stack>
                </Stack>
                {view === 'Agent Hub' ? (
                  <AgentHubOffers
                    rows={model.agentHubOffers}
                    refund={filters.refund}
                    sort={params.get('offerSort') ?? 'source'}
                    direction={params.get('offerDir') === 'desc' ? 'desc' : 'asc'}
                    page={offerPage}
                    onPage={(page) => setDisplay({ offerPage: String(page) })}
                    onSort={(key) => sortTable('offer', key)}
                  />
                ) : (
                  <CommercialKpiOffers
                    rows={model.displayedOfferRows}
                    view={view}
                    sort={params.get('offerSort') ?? 'source'}
                    direction={params.get('offerDir') === 'desc' ? 'desc' : 'asc'}
                    page={offerPage}
                    onPage={(page) => setDisplay({ offerPage: String(page) })}
                    onSort={(key) => sortTable('offer', key)}
                    onDetail={(offer) => {
                      if (ready) {
                        setOfferDetail(offer);
                      }
                    }}
                  />
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </Box>
      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Filters{activeCount ? ` · ${activeCount} active` : ''}</DialogTitle>
        <DialogContent>
          <CommercialKpiFilters
            filters={filters}
            options={targetModel.options}
            view={view}
            onChange={update}
            onReset={() => update(defaults(now))}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setFiltersOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={methodOpen} onClose={() => setMethodOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Method and sources</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Commercial KPI Tracker, Tableau workbook PCMTrackernew_17401097654120 (October 2026 download). This page
              ports its PCM, MM, Experience and Agent Hub dashboards, each read with exactly one report_view. The
              Subscriptions and Subscriptions - MM dashboards are email layouts of the PCM and MM Targets sheets and are
              not ported; a scheduled email covers them.
            </Typography>
            <Typography>
              Experience: Net Margin (Experience) = GM (Experience) on the chosen refund basis minus ICS cost margin
              reduction; TTV and Refunds (Experience) are the adjusted source fields. Agent Hub: each BDM row repeats
              the full team net margin (GM adjusted AR), as Tableau shows it. The team total and the KPI charts take it
              once from the agent_hub_team card and never sum the BDM copies; activity counts are summed across BDMs.
              Agent Hub offers come from analytics.bdm_tracker_offers_ah.
            </Typography>
            <Typography>
              Live authenticated Lens operands from analytics.pcm_tracker_unpivot, analytics.pcm_tracker_offers,
              analytics.bdm_tracker_offers_ah and stage.stage_pcm_targets. Source refresh/evaluation time is not
              supplied and remains Unknown. The forecast clock is {settled.forecastAsOf} in Australia/Sydney.
            </Typography>
            <Typography>
              Fiscal year begins in July. Quarter filtering uses quarter_start_date. Actual, target and override
              operands retain their source values; missing overrides remain Unknown. The original owner/region hierarchy
              and each worksheet's metric rows are preserved (PCM adds Reopened Signings and Total Activity; MM adds
              MpOI, MpV, Activated Agents and Total Activity; Experience shows Net Margin, Net Signings, Proposals and
              Total Activity; Agent Hub shows Net Margin, Activated Agents and Total Activity). No selected quarter rows
              is a legitimate empty result, distinct from failed/incomplete data.
            </Typography>
            {Object.entries(FORMULAS).map(([name, formula]) => (
              <Typography key={name}>
                <Box component="strong">{name}: </Box>
                {formula}
              </Typography>
            ))}
            <Typography>
              Refund basis switches offer GM, TTV and their adjusted variants. Adjusted net margin remains the source
              net_margin_adjusted field. PCM retains the original fixed after-refund source LP addback column. The offer
              detail dialog also exposes the selected-basis addback from source loss and gain components; a missing
              component remains Unknown. MM omits the addback column because its original measure filter excludes it.
            </Typography>
            <Typography>
              Offer records are grouped on the older worksheet’s visible dimensions before display. PCM includes owner,
              country, opportunity, status, product alias and integer margin forecast; MM includes owner, PCM region,
              country, opportunity and product alias. Numeric measures use null-preserving sums. Multiple IDs, names or
              dates remain unassigned unless unique. Refunds are summed from valid per-source-row differences, so
              mismatched missing operands do not create a number. Original-order subtotals cover the entire filtered
              owner group across pagination; MM also has region subtotals. Flat custom sorting omits subtotal rows until
              Original order is restored.
            </Typography>
            <Typography>
              Offer names use offer_name for Tourbank and Cruisebank, and offer_opportunity_name otherwise. Status uses
              source start/end dates against the injected Sydney date, with end date treated as ended.
            </Typography>
            <Typography>
              Charts use LE Ocean Teal for actuals, Coral Green for forecast and Jet Black for targets. The native page
              uses MUI under the standard’s native Admin exception. Global filters, team, sorting and selection persist
              in the URL. Progress rows select exact owner/region tuples in both sources; Ctrl, ⌘ or Shift adds or
              removes tuples. The source progress table stays visible to support multiple selections. Tuple predicates
              apply at the complete owner/region aggregate grain and preserve the original PCM/MM projection.
            </Typography>
            <Typography>
              <Box component="strong">Source reconciliation: </Box>
              Offer net margin A${format(model.offerTotals.netMargin, 'money')} and KPI net margin A$
              {format(model.metrics.find((metric) => metric.id === 'net_margin_adjusted')?.actual, 'money')} are
              separate source populations; reconcile before treating them as interchangeable.
            </Typography>
            <Typography>
              Figures to verify: parity against live Tableau rendered marks and refresh-to-refresh warehouse changes
              remains a separate UAT check. This page follows the workbook dashboard structure, target-row hierarchies,
              bullet charts and offer measure shelves of each view. Global controls currently select one value each,
              unlike Tableau checkdropdown multi-select. Lists come from the live query period rather than Tableau
              relevant-only lists; long name fields show the first 100 search matches. Exact target-row tuple selection
              supports multiple marks. Tableau PDF/email actions and production refresh are not implemented. The
              original header formula is retained as workbook text; source net_margin_adjusted may contain additional
              source-defined components.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMethodOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!offerDetail && ready && offerDetailIdentity === settled.identity}
        onClose={() => setOfferDetail(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{offerDetail?.displayName}</DialogTitle>
        <DialogContent>
          {offerDetail && (
            <Stack spacing={1}>
              <Typography>
                {offerDetail.offer_bk ??
                  (offerDetail.identityConflicts.includes('offer_bk') ? 'Multiple source offer IDs' : 'Unknown')}{' '}
                · {offerDetail.brand_name ?? 'Unknown'} · {offerDetail.pcm_name ?? 'Unknown'} · {filters.refund}
              </Typography>
              <Typography variant="body2">
                This displayed row aggregates {offerDetail.sourceRows} aggregate records. Identity fields remain Unknown
                unless unique.
                {offerDetail.identityConflicts.length > 0
                  ? ` Multiple source values: ${offerDetail.identityConflicts.join(', ')}.`
                  : ''}
              </Typography>
              <Typography variant="caption">
                All margin components below are A$. Unknown preserves missing source values.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source component</TableCell>
                      <TableCell align="right">A$</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Adjusted net margin', offerDetail.net_margin_adjusted],
                      ['Promotions used', offerDetail.promo_used_aud],
                      ['Source LP margin addback (after refund)', offerDetail.lp_margin_add_back_ar],
                      ['Selected-basis LP addback', offerDetail.addback],
                      ['ICS cost margin reduction', offerDetail.ics_cost_margin_reduction_aud],
                      ['PCM tour addback', offerDetail.pcm_tour_addback],
                      ['10% PCM tour margin reduction', offerDetail.ten_percent_pcm_tour_margin_reduction],
                      ['LP loss on sale', offerDetail.lp_margin_loss_sale],
                      ['LP gain on sale', offerDetail.lp_margin_gain_sale],
                      ['LP loss on refund', offerDetail.lp_margin_loss_refund],
                      ['LP gain on refund', offerDetail.lp_margin_gain_refund],
                    ].map(([name, value]) => (
                      <TableRow key={String(name)}>
                        <TableCell>{name}</TableCell>
                        <TableCell align="right">{format(value as number | null, 'money')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography>
                Salesperson margin forecast: {offerDetail.salesperson_margin_forecast ?? 'Unknown'}
              </Typography>
              <Accordion
                disableGutters
                elevation={0}
                slotProps={{ transition: { unmountOnExit: true } }}
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Underlying aggregate rows · {underlyingOffers.length}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Button
                      disabled={!ready}
                      onClick={() =>
                        downloadCsv(
                          underlyingOffers,
                          `commercial-kpi-${view.toLowerCase()}-source-rows-${filters.quarter}.csv`,
                        )
                      }
                    >
                      Export these aggregate rows
                    </Button>
                    <Typography variant="caption">
                      Showing up to 200 rows. CSV contains the full source group. Money is A$; dates retain source
                      calendar definitions.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 350, overflow: 'auto' }}>
                      <Table
                        size="small"
                        stickyHeader
                        sx={{
                          minWidth: 950,
                          '& .MuiTableCell-root': { fontSize: 12 },
                          '& .MuiTableCell-head': { bgcolor: BRAND_COLORS.oceanTeal, color: BRAND_COLORS.compassWhite },
                        }}
                        aria-label="Underlying offer source rows"
                      >
                        <TableHead>
                          <TableRow>
                            {[
                              'Offer ID',
                              'Offer name',
                              'PCM region',
                              'RSM region',
                              'Start',
                              'End',
                              'GM',
                              'TTV',
                              'Net margin adjusted',
                            ].map((label) => (
                              <TableCell key={label}>{label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {underlyingOffers.slice(0, 200).map((row) => (
                            <TableRow key={row.key}>
                              <TableCell>{row.offer_bk ?? 'Unknown'}</TableCell>
                              <TableCell>{row.offer_name ?? 'Unknown'}</TableCell>
                              <TableCell>{row.pcm_region ?? 'Unknown'}</TableCell>
                              <TableCell>{row.rsm_region ?? 'Unknown'}</TableCell>
                              <TableCell>{row.offer_start_date ?? 'Unknown'}</TableCell>
                              <TableCell>{row.offer_end_date ?? 'Unknown'}</TableCell>
                              <TableCell align="right">{format(row.gm, 'money')}</TableCell>
                              <TableCell align="right">{format(row.ttv, 'money')}</TableCell>
                              <TableCell align="right">{format(row.net_margin_adjusted, 'money')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
